/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  AddProductWalletRequest,
  AddWalletRequestRequest,
  AvailableProduct,
  InvestorPortfolio,
  PaginatedDropdownElementList,
  PaginatedInvestorPortfolioList,
  PaginatedProductWalletAssignmentStatusList,
  ProductWalletAssignmentStatus,
  Wallet,
} from '../models/index';
import {
    AddProductWalletRequestFromJSON,
    AddProductWalletRequestToJSON,
    AddWalletRequestRequestFromJSON,
    AddWalletRequestRequestToJSON,
    AvailableProductFromJSON,
    AvailableProductToJSON,
    InvestorPortfolioFromJSON,
    InvestorPortfolioToJSON,
    PaginatedDropdownElementListFromJSON,
    PaginatedDropdownElementListToJSON,
    PaginatedInvestorPortfolioListFromJSON,
    PaginatedInvestorPortfolioListToJSON,
    PaginatedProductWalletAssignmentStatusListFromJSON,
    PaginatedProductWalletAssignmentStatusListToJSON,
    ProductWalletAssignmentStatusFromJSON,
    ProductWalletAssignmentStatusToJSON,
    WalletFromJSON,
    WalletToJSON,
} from '../models/index';

export interface PortfolioAddProductCreateRequest {
    addProductWalletRequest: AddProductWalletRequest;
}

export interface PortfolioAddWalletCreateRequest {
    productId: string;
    addWalletRequestRequest: AddWalletRequestRequest;
}

export interface PortfolioAssignmentsCancelCreateRequest {
    assignmentId: string;
}

export interface PortfolioAssignmentsStatusRetrieveRequest {
    assignmentId: string;
}

export interface PortfolioAvailableProductsListRequest {
    ordering?: PortfolioAvailableProductsListOrderingEnum;
}

export interface PortfolioAvailableProductsWalletsListRequest {
    productId: string;
    ordering?: PortfolioAvailableProductsWalletsListOrderingEnum;
}

export interface PortfolioDropdownListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    field?: PortfolioDropdownListFieldEnum;
    id?: string;
    lastProfitMax?: number;
    lastProfitMin?: number;
    limit?: number;
    nextProfitAfter?: Date;
    nextProfitBefore?: Date;
    offset?: number;
    ordering?: PortfolioDropdownListOrderingEnum;
    productName?: string;
    totalEarningsMax?: number;
    totalEarningsMin?: number;
    totalInvestmentAmountMax?: number;
    totalInvestmentAmountMin?: number;
}

export interface PortfolioListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    id?: Array<string>;
    lastProfitMax?: number;
    lastProfitMin?: number;
    limit?: number;
    nextProfitAfter?: Date;
    nextProfitBefore?: Date;
    offset?: number;
    ordering?: PortfolioListOrderingEnum;
    productName?: Array<string>;
    totalEarningsMax?: number;
    totalEarningsMin?: number;
    totalInvestmentAmountMax?: number;
    totalInvestmentAmountMin?: number;
}

export interface PortfolioProductAssignmentsListRequest {
    limit?: number;
    offset?: number;
    ordering?: PortfolioProductAssignmentsListOrderingEnum;
}

export interface PortfolioRetrieveRequest {
    id: string;
}

export interface PortfolioValuesRetrieveRequest {
    field: PortfolioValuesRetrieveFieldEnum;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    icontains?: string;
    id?: string;
    lastProfitMax?: number;
    lastProfitMin?: number;
    nextProfitAfter?: Date;
    nextProfitBefore?: Date;
    ordering?: PortfolioValuesRetrieveOrderingEnum;
    productName?: string;
    totalEarningsMax?: number;
    totalEarningsMin?: number;
    totalInvestmentAmountMax?: number;
    totalInvestmentAmountMin?: number;
}

/**
 * 
 */
export class PortfolioApi extends runtime.BaseAPI {

    /**
     * Add a product + wallet to the investor\'s portfolio (creates ProductWalletAssignment).
     */
    async portfolioAddProductCreateRaw(requestParameters: PortfolioAddProductCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ProductWalletAssignmentStatus>> {
        if (requestParameters['addProductWalletRequest'] == null) {
            throw new runtime.RequiredError(
                'addProductWalletRequest',
                'Required parameter "addProductWalletRequest" was null or undefined when calling portfolioAddProductCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/portfolio/add-product/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: AddProductWalletRequestToJSON(requestParameters['addProductWalletRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ProductWalletAssignmentStatusFromJSON(jsonValue));
    }

    /**
     * Add a product + wallet to the investor\'s portfolio (creates ProductWalletAssignment).
     */
    async portfolioAddProductCreate(requestParameters: PortfolioAddProductCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ProductWalletAssignmentStatus> {
        const response = await this.portfolioAddProductCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Add a wallet to an existing product in the portfolio.
     */
    async portfolioAddWalletCreateRaw(requestParameters: PortfolioAddWalletCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ProductWalletAssignmentStatus>> {
        if (requestParameters['productId'] == null) {
            throw new runtime.RequiredError(
                'productId',
                'Required parameter "productId" was null or undefined when calling portfolioAddWalletCreate().'
            );
        }

        if (requestParameters['addWalletRequestRequest'] == null) {
            throw new runtime.RequiredError(
                'addWalletRequestRequest',
                'Required parameter "addWalletRequestRequest" was null or undefined when calling portfolioAddWalletCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/portfolio/{product_id}/add-wallet/`.replace(`{${"product_id"}}`, encodeURIComponent(String(requestParameters['productId']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: AddWalletRequestRequestToJSON(requestParameters['addWalletRequestRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ProductWalletAssignmentStatusFromJSON(jsonValue));
    }

    /**
     * Add a wallet to an existing product in the portfolio.
     */
    async portfolioAddWalletCreate(requestParameters: PortfolioAddWalletCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ProductWalletAssignmentStatus> {
        const response = await this.portfolioAddWalletCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Cancel a pending ProductWalletAssignment.
     */
    async portfolioAssignmentsCancelCreateRaw(requestParameters: PortfolioAssignmentsCancelCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['assignmentId'] == null) {
            throw new runtime.RequiredError(
                'assignmentId',
                'Required parameter "assignmentId" was null or undefined when calling portfolioAssignmentsCancelCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/assignments/{assignment_id}/cancel/`.replace(`{${"assignment_id"}}`, encodeURIComponent(String(requestParameters['assignmentId']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Cancel a pending ProductWalletAssignment.
     */
    async portfolioAssignmentsCancelCreate(requestParameters: PortfolioAssignmentsCancelCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.portfolioAssignmentsCancelCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Poll the status of a ProductWalletAssignment (trustline progress).
     */
    async portfolioAssignmentsStatusRetrieveRaw(requestParameters: PortfolioAssignmentsStatusRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ProductWalletAssignmentStatus>> {
        if (requestParameters['assignmentId'] == null) {
            throw new runtime.RequiredError(
                'assignmentId',
                'Required parameter "assignmentId" was null or undefined when calling portfolioAssignmentsStatusRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/assignments/{assignment_id}/status/`.replace(`{${"assignment_id"}}`, encodeURIComponent(String(requestParameters['assignmentId']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ProductWalletAssignmentStatusFromJSON(jsonValue));
    }

    /**
     * Poll the status of a ProductWalletAssignment (trustline progress).
     */
    async portfolioAssignmentsStatusRetrieve(requestParameters: PortfolioAssignmentsStatusRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ProductWalletAssignmentStatus> {
        const response = await this.portfolioAssignmentsStatusRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List products on the investor\'s distribution platform that are not yet in the portfolio.
     */
    async portfolioAvailableProductsListRaw(requestParameters: PortfolioAvailableProductsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<AvailableProduct>>> {
        const queryParameters: any = {};

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/available-products/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(AvailableProductFromJSON));
    }

    /**
     * List products on the investor\'s distribution platform that are not yet in the portfolio.
     */
    async portfolioAvailableProductsList(requestParameters: PortfolioAvailableProductsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<AvailableProduct>> {
        const response = await this.portfolioAvailableProductsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List investor\'s wallets compatible with the product\'s network, not already assigned.
     */
    async portfolioAvailableProductsWalletsListRaw(requestParameters: PortfolioAvailableProductsWalletsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<Wallet>>> {
        if (requestParameters['productId'] == null) {
            throw new runtime.RequiredError(
                'productId',
                'Required parameter "productId" was null or undefined when calling portfolioAvailableProductsWalletsList().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/available-products/{product_id}/wallets/`.replace(`{${"product_id"}}`, encodeURIComponent(String(requestParameters['productId']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(WalletFromJSON));
    }

    /**
     * List investor\'s wallets compatible with the product\'s network, not already assigned.
     */
    async portfolioAvailableProductsWalletsList(requestParameters: PortfolioAvailableProductsWalletsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<Wallet>> {
        const response = await this.portfolioAvailableProductsWalletsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async portfolioDropdownListRaw(requestParameters: PortfolioDropdownListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDropdownElementList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['lastProfitMax'] != null) {
            queryParameters['last_profit_max'] = requestParameters['lastProfitMax'];
        }

        if (requestParameters['lastProfitMin'] != null) {
            queryParameters['last_profit_min'] = requestParameters['lastProfitMin'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['nextProfitAfter'] != null) {
            queryParameters['next_profit_after'] = (requestParameters['nextProfitAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['nextProfitBefore'] != null) {
            queryParameters['next_profit_before'] = (requestParameters['nextProfitBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['totalEarningsMax'] != null) {
            queryParameters['total_earnings_max'] = requestParameters['totalEarningsMax'];
        }

        if (requestParameters['totalEarningsMin'] != null) {
            queryParameters['total_earnings_min'] = requestParameters['totalEarningsMin'];
        }

        if (requestParameters['totalInvestmentAmountMax'] != null) {
            queryParameters['total_investment_amount_max'] = requestParameters['totalInvestmentAmountMax'];
        }

        if (requestParameters['totalInvestmentAmountMin'] != null) {
            queryParameters['total_investment_amount_min'] = requestParameters['totalInvestmentAmountMin'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/dropdown/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDropdownElementListFromJSON(jsonValue));
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async portfolioDropdownList(requestParameters: PortfolioDropdownListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDropdownElementList> {
        const response = await this.portfolioDropdownListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List the portfolio entries of the requesting user for all products.
     */
    async portfolioListRaw(requestParameters: PortfolioListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedInvestorPortfolioList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['lastProfitMax'] != null) {
            queryParameters['last_profit_max'] = requestParameters['lastProfitMax'];
        }

        if (requestParameters['lastProfitMin'] != null) {
            queryParameters['last_profit_min'] = requestParameters['lastProfitMin'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['nextProfitAfter'] != null) {
            queryParameters['next_profit_after'] = (requestParameters['nextProfitAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['nextProfitBefore'] != null) {
            queryParameters['next_profit_before'] = (requestParameters['nextProfitBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['totalEarningsMax'] != null) {
            queryParameters['total_earnings_max'] = requestParameters['totalEarningsMax'];
        }

        if (requestParameters['totalEarningsMin'] != null) {
            queryParameters['total_earnings_min'] = requestParameters['totalEarningsMin'];
        }

        if (requestParameters['totalInvestmentAmountMax'] != null) {
            queryParameters['total_investment_amount_max'] = requestParameters['totalInvestmentAmountMax'];
        }

        if (requestParameters['totalInvestmentAmountMin'] != null) {
            queryParameters['total_investment_amount_min'] = requestParameters['totalInvestmentAmountMin'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedInvestorPortfolioListFromJSON(jsonValue));
    }

    /**
     * List the portfolio entries of the requesting user for all products.
     */
    async portfolioList(requestParameters: PortfolioListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedInvestorPortfolioList> {
        const response = await this.portfolioListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List all ProductWalletAssignments for the requesting investor.
     */
    async portfolioProductAssignmentsListRaw(requestParameters: PortfolioProductAssignmentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedProductWalletAssignmentStatusList>> {
        const queryParameters: any = {};

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/product-assignments/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedProductWalletAssignmentStatusListFromJSON(jsonValue));
    }

    /**
     * List all ProductWalletAssignments for the requesting investor.
     */
    async portfolioProductAssignmentsList(requestParameters: PortfolioProductAssignmentsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedProductWalletAssignmentStatusList> {
        const response = await this.portfolioProductAssignmentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get the portfolio entry of the requesting user of the given product.
     */
    async portfolioRetrieveRaw(requestParameters: PortfolioRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestorPortfolio>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling portfolioRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestorPortfolioFromJSON(jsonValue));
    }

    /**
     * Get the portfolio entry of the requesting user of the given product.
     */
    async portfolioRetrieve(requestParameters: PortfolioRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestorPortfolio> {
        const response = await this.portfolioRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async portfolioValuesRetrieveRaw(requestParameters: PortfolioValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<string>>> {
        if (requestParameters['field'] == null) {
            throw new runtime.RequiredError(
                'field',
                'Required parameter "field" was null or undefined when calling portfolioValuesRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['icontains'] != null) {
            queryParameters['icontains'] = requestParameters['icontains'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['lastProfitMax'] != null) {
            queryParameters['last_profit_max'] = requestParameters['lastProfitMax'];
        }

        if (requestParameters['lastProfitMin'] != null) {
            queryParameters['last_profit_min'] = requestParameters['lastProfitMin'];
        }

        if (requestParameters['nextProfitAfter'] != null) {
            queryParameters['next_profit_after'] = (requestParameters['nextProfitAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['nextProfitBefore'] != null) {
            queryParameters['next_profit_before'] = (requestParameters['nextProfitBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['totalEarningsMax'] != null) {
            queryParameters['total_earnings_max'] = requestParameters['totalEarningsMax'];
        }

        if (requestParameters['totalEarningsMin'] != null) {
            queryParameters['total_earnings_min'] = requestParameters['totalEarningsMin'];
        }

        if (requestParameters['totalInvestmentAmountMax'] != null) {
            queryParameters['total_investment_amount_max'] = requestParameters['totalInvestmentAmountMax'];
        }

        if (requestParameters['totalInvestmentAmountMin'] != null) {
            queryParameters['total_investment_amount_min'] = requestParameters['totalInvestmentAmountMin'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/portfolio/values/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async portfolioValuesRetrieve(requestParameters: PortfolioValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<string>> {
        const response = await this.portfolioValuesRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

}

/**
 * @export
 */
export const PortfolioAvailableProductsListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    LAST_PROFIT_DESC: '-last_profit',
    LAST_PROFIT_ASC: 'last_profit',
    NEXT_PROFIT_DESC: '-next_profit',
    NEXT_PROFIT_ASC: 'next_profit',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    TOTAL_EARNINGS_DESC: '-total_earnings',
    TOTAL_EARNINGS_ASC: 'total_earnings',
    TOTAL_INVESTMENT_AMOUNT_DESC: '-total_investment_amount',
    TOTAL_INVESTMENT_AMOUNT_ASC: 'total_investment_amount'
} as const;
export type PortfolioAvailableProductsListOrderingEnum = typeof PortfolioAvailableProductsListOrderingEnum[keyof typeof PortfolioAvailableProductsListOrderingEnum];
/**
 * @export
 */
export const PortfolioAvailableProductsWalletsListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    LAST_PROFIT_DESC: '-last_profit',
    LAST_PROFIT_ASC: 'last_profit',
    NEXT_PROFIT_DESC: '-next_profit',
    NEXT_PROFIT_ASC: 'next_profit',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    TOTAL_EARNINGS_DESC: '-total_earnings',
    TOTAL_EARNINGS_ASC: 'total_earnings',
    TOTAL_INVESTMENT_AMOUNT_DESC: '-total_investment_amount',
    TOTAL_INVESTMENT_AMOUNT_ASC: 'total_investment_amount'
} as const;
export type PortfolioAvailableProductsWalletsListOrderingEnum = typeof PortfolioAvailableProductsWalletsListOrderingEnum[keyof typeof PortfolioAvailableProductsWalletsListOrderingEnum];
/**
 * @export
 */
export const PortfolioDropdownListFieldEnum = {
    PRODUCT_NAME: 'product_name'
} as const;
export type PortfolioDropdownListFieldEnum = typeof PortfolioDropdownListFieldEnum[keyof typeof PortfolioDropdownListFieldEnum];
/**
 * @export
 */
export const PortfolioDropdownListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    LAST_PROFIT_DESC: '-last_profit',
    LAST_PROFIT_ASC: 'last_profit',
    NEXT_PROFIT_DESC: '-next_profit',
    NEXT_PROFIT_ASC: 'next_profit',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    TOTAL_EARNINGS_DESC: '-total_earnings',
    TOTAL_EARNINGS_ASC: 'total_earnings',
    TOTAL_INVESTMENT_AMOUNT_DESC: '-total_investment_amount',
    TOTAL_INVESTMENT_AMOUNT_ASC: 'total_investment_amount'
} as const;
export type PortfolioDropdownListOrderingEnum = typeof PortfolioDropdownListOrderingEnum[keyof typeof PortfolioDropdownListOrderingEnum];
/**
 * @export
 */
export const PortfolioListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    LAST_PROFIT_DESC: '-last_profit',
    LAST_PROFIT_ASC: 'last_profit',
    NEXT_PROFIT_DESC: '-next_profit',
    NEXT_PROFIT_ASC: 'next_profit',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    TOTAL_EARNINGS_DESC: '-total_earnings',
    TOTAL_EARNINGS_ASC: 'total_earnings',
    TOTAL_INVESTMENT_AMOUNT_DESC: '-total_investment_amount',
    TOTAL_INVESTMENT_AMOUNT_ASC: 'total_investment_amount'
} as const;
export type PortfolioListOrderingEnum = typeof PortfolioListOrderingEnum[keyof typeof PortfolioListOrderingEnum];
/**
 * @export
 */
export const PortfolioProductAssignmentsListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    LAST_PROFIT_DESC: '-last_profit',
    LAST_PROFIT_ASC: 'last_profit',
    NEXT_PROFIT_DESC: '-next_profit',
    NEXT_PROFIT_ASC: 'next_profit',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    TOTAL_EARNINGS_DESC: '-total_earnings',
    TOTAL_EARNINGS_ASC: 'total_earnings',
    TOTAL_INVESTMENT_AMOUNT_DESC: '-total_investment_amount',
    TOTAL_INVESTMENT_AMOUNT_ASC: 'total_investment_amount'
} as const;
export type PortfolioProductAssignmentsListOrderingEnum = typeof PortfolioProductAssignmentsListOrderingEnum[keyof typeof PortfolioProductAssignmentsListOrderingEnum];
/**
 * @export
 */
export const PortfolioValuesRetrieveFieldEnum = {
    PRODUCT_NAME: 'product_name'
} as const;
export type PortfolioValuesRetrieveFieldEnum = typeof PortfolioValuesRetrieveFieldEnum[keyof typeof PortfolioValuesRetrieveFieldEnum];
/**
 * @export
 */
export const PortfolioValuesRetrieveOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    LAST_PROFIT_DESC: '-last_profit',
    LAST_PROFIT_ASC: 'last_profit',
    NEXT_PROFIT_DESC: '-next_profit',
    NEXT_PROFIT_ASC: 'next_profit',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    TOTAL_EARNINGS_DESC: '-total_earnings',
    TOTAL_EARNINGS_ASC: 'total_earnings',
    TOTAL_INVESTMENT_AMOUNT_DESC: '-total_investment_amount',
    TOTAL_INVESTMENT_AMOUNT_ASC: 'total_investment_amount'
} as const;
export type PortfolioValuesRetrieveOrderingEnum = typeof PortfolioValuesRetrieveOrderingEnum[keyof typeof PortfolioValuesRetrieveOrderingEnum];
